import React, { useMemo } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import AnimatedArcs from '../../common/AnimatedArcs';
import {
	ICareersFilterProps,
	memoizeOptionsForReactSelect,
} from '../CareerCardGroup';
import Select from '../../common/ReactSelect';

export interface ICareersIndexHeroAreaProps extends IBaseElementProps {
	/**
	 * Heading text - bold text will have color gradient applied
	 */
	heading?: string;
	/**
	 * Line Animation Delay in Milliseconds for use with Default and Content Hero Styles
	 */
	lineAnimationDelay?: number;
	/**
	 * Line Animation Offset in Pixels for use with Default and Content Hero Styles
	 */
	lineAnimationOffset?: number;
	filters: ICareersFilterProps[];
	filterValues?: {
		[key: string]: string;
	};
	onChangeFilter?: (taxonomy: string, value: string) => void;
	searchPosition?: 'Banner' | 'Filter Area';
	searchPlaceholder?: string;
	remoteCheckboxText?: string;
}

const CareersIndexHeroArea: React.FC<ICareersIndexHeroAreaProps> = ({
	id,
	className,
	classNames = [],
	heading,
	lineAnimationDelay,
	lineAnimationOffset,
	filters,
	filterValues = {},
	onChangeFilter = () => {},
	searchPosition = 'Filter Area',
	searchPlaceholder = 'Search...',
	remoteCheckboxText,
}) => {
	const [filterOptions, searchConfig] = useMemo(
		() => memoizeOptionsForReactSelect(filters),
		[filters],
	);
	return (
		<div
			id={id}
			className={
				className || cn('calian-careers-index-hero-area', ...classNames)
			}
		>
			<div className="calian-careers-index-hero-area__container container-fluid">
				<div className="row">
					<div className="calian-careers-index-hero-area__top col-12">
						<div className="calian-careers-index-hero-area__content">
							{heading && (
								<h1 className="calian-careers-index-hero-area__heading">
									{heading}
								</h1>
							)}
							{searchPosition === 'Banner' && (
								<div className="calian-careers-index-hero-area__search">
									<div className="calian-careers-index-hero-area__search-form d-flex">
										<input
											className="form-control"
											type="text"
											placeholder={searchPlaceholder}
											aria-label={searchPlaceholder}
											value={filterValues['search'] || ''}
											onChange={(e) =>
												onChangeFilter(
													'search',
													e.target.value,
												)
											}
										/>
										<button className="btn" type="submit">
											<i className="icon icon-magnifying-glass" />
											<span className="visually-hidden">
												{searchPlaceholder}
											</span>
										</button>
									</div>
								</div>
							)}
						</div>
						<AnimatedArcs
							arcsStyle={'Component - Two Lines'}
							arcsPosition="Right"
							arcOneColor="var(--bs-light)"
							arcOneStrokeWidth={4}
							arcTwoColor="var(--bs-white)"
							arcTwoStrokeWidth={3}
							animationDelay={lineAnimationDelay}
							animationOffset={lineAnimationOffset}
						/>
					</div>
					<div className="calian-careers-index-hero-area__bottom col-12">
						<div className="calian-careers-index-hero-area__filter">
							<div className="calian-careers-index-hero-area__filter-container container-fluid">
								<div className="row">
									{searchPosition === 'Filter Area' && (
										<div className="col-12 col-sm-3 col-lg-auto">
											<div className="calian-careers-index-hero-area__search">
												<div className="calian-careers-index-hero-area__search-form d-flex">
													<input
														className="form-control"
														type="text"
														placeholder={
															searchPlaceholder
														}
														aria-label={
															searchPlaceholder
														}
														value={
															filterValues[
																'search'
															] || ''
														}
														onChange={(e) =>
															onChangeFilter(
																'search',
																e.target.value,
															)
														}
													/>
													<button
														className="btn"
														type="submit"
													>
														<i className="icon icon-magnifying-glass" />
														<span className="visually-hidden">
															{searchPlaceholder}
														</span>
													</button>
												</div>
											</div>
										</div>
									)}

									{(filters || []).map((filter) => {
										return (
											<div
												className="col-6 col-sm-3 col-lg-auto"
												key={filter.taxonomy}
											>
												<div className="calian-careers-index-hero-area__filter-dropdown">
													<Select
														className="form-select react-form-select"
														classNamePrefix="react-select"
														isSearchable
														isClearable
														placeholder={
															filter.label
														}
														options={Object.values(
															filterOptions[
																filter.taxonomy
															],
														)}
														onChange={(o: any) => {
															onChangeFilter(
																filter.taxonomy,
																o?.value,
															);
														}}
														aria-label={
															filter.label
														}
														value={
															filterOptions[
																filter.taxonomy
															][
																filterValues[
																	filter
																		.taxonomy
																] || ''
															]
														}
														filterOption={
															searchConfig as (
																option: any,
																rawInput: string,
															) => boolean
														}
													/>
												</div>
											</div>
										);
									})}
									<div className="checkbox-column col-6 col-sm-3 col-lg-auto">
										<div className="calian-careers-index-hero-area__filter-checkbox">
											<label
												className="form-check-label"
												htmlFor="remote-checkbox"
											>
												<input
													className="form-check-input"
													type="checkbox"
													checked={
														!!filterValues['remote']
													}
													id="remote-checkbox"
													onChange={(e) =>
														onChangeFilter(
															'remote',
															e.target.checked
																? '1'
																: null,
														)
													}
												/>
												<span className="custom-checkmark"></span>
												{remoteCheckboxText}
											</label>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default CareersIndexHeroArea;
